<template lang="pug">
    div.mx-lg-auto(width="100%")
        template(v-if="validTranscription")
            v-card.ma-2(v-if="!hideSpeakerTimeChart && validSpeakerTime" :outlined="isOutlined")
                v-card-title Speaker Time
                v-card-subtitle (in percentage)
                v-card-text
                    pie-chart(
                        :data="speakerTime"
                        :colors="['var(--warning)', 'var(--volie-blue)', 'var(--off-white)', 'var(--lt-red)c']"
                        plugins=[chartjsPluginDatalabels]
                    )

            v-card(:outlined="isOutlined")
                v-card-title(style="font-size: 1rem;")
                    | Transcript
                    div.audio-wrapper.ml-auto.width-100-percent
                        audio#audio-playback(
                            controls
                            :src="conference.recording_url"
                        )
                v-card-text#transcription-holder(style="max-height: 400px; overflow-y: auto;")
                    //- v-row.transcription-segment.ma-0.grey-side-border(v-for="utterance in callTranscription.results.utterances" :key="utterance.start" :class="isActiveRecordingSegment(utterance)")
                    v-row.transcription-segment.ma-0(v-for="utterance in callTranscription.utterances" :key="utterance.start" :class="isActiveRecordingSegment(utterance)")
                        v-col.transcript-content(cols='12')
                            .participant Speaker {{ utterance.speaker + 1 }} -
                                span(class="time-seek" @click="setTimeOfPlayback(utterance.start)") {{ setTimeInSeconds(utterance.start)}}
                            .content {{utterance.transcript}}


                    //- v-row.transcription-segment.ma-0.grey.lighten-2(style="padding: 1px 0px;")


        div(v-if="!validTranscription")
            v-alert(outlined type='info' dense border='left' style="margin-top: 16px;")
                | There is no call transcription for this conference

</template>

<script lang="ts">
import Vue from 'vue';

import Chartkick from 'vue-chartkick';
import Chart from 'chart.js';

Vue.use(Chartkick.use(Chart));

// Components
import SentimentIcon from '@/components/call_transcriptions/sentiment_icon.vue';

export default Vue.extend({
    data() {
        return {
            audio: null,
            agentDecibels: [],
            compatibleTranscription: true,
            customerDecibels: [],
            currentSeconds: 0,
            displayStyle: 'normal',
            issues: [],
            playing: false,
            sentiments: [],
            speakerTime: [],
            transcription: [],
            isAutoScrolling: false,
            userInteracting: false,
            userInteractTimeout: null,
            autoScrollEnabled: true,
        }
    },
    methods: {
        animateScroll(element, to, duration = 300) {
            this.isAutoScrolling = true;
            const start = element.scrollTop;
            const change = to - start;
            const startTime = performance.now();
            const self = this;
            function animateScrollStep(now) {
                const elapsed = now - startTime;
                const progress = Math.min(elapsed / duration, 1);
                element.scrollTop = start + change * progress;
                if (progress < 1) {
                    requestAnimationFrame(animateScrollStep);
                } else {
                    self.isAutoScrolling = false;
                }
            }
            requestAnimationFrame(animateScrollStep);
        },
        onTranscriptionScroll(event) {
            if (this.isAutoScrolling) {
                // Ignore scroll events triggered by auto scroll
                return;
            }
            if (this.userInteracting) {
                // User-initiated scroll
                this.autoScrollEnabled = false;
                // console.log('User scrolled', event.target.scrollTop);
            }
        },
        onUserInteract() {
            this.userInteracting = true;
            if (this.userInteractTimeout) {
                clearTimeout(this.userInteractTimeout);
            }
            this.userInteractTimeout = setTimeout(() => {
                this.userInteracting = false;
            }, 500);
        },
        isActiveRecordingSegment(utterance) {
            const beginningSeconds = (utterance.start).toFixed(0)
            const endingSeconds = (utterance.end).toFixed(0)
            if (this.currentSeconds >= beginningSeconds && this.currentSeconds <= endingSeconds) {
                return "active-segment"
            } else {
                return "inactive-segment"
            }
        },
        hasIssues(segment) {
            return segment?.IssuesDetected?.length > 0
        },
        setParticipant(name) {

            let cleanedName = name

            if(this.displayStyle === 'flipped') {
                if (name === 'CUSTOMER') {
                    cleanedName = 'AGENT'
                }
                if (name === 'AGENT') {
                    cleanedName = 'CUSTOMER'
                }
            }

            return cleanedName.toLowerCase()
        },
        setSpeakerTime(){

            // this.callTranscription.results.utterances.forEach((utterance) => {
            //     const participant = this.setParticipant(utterance.speaker)
            //     const time = utterance.end - utterance.start
            //     if (this.speakerTime.length === 0) {
            //         this.speakerTime.push([participant, time])
            //     } else {
            //         const index = this.speakerTime.findIndex((element) => element[0] === participant)
            //         if (index === -1) {
            //             this.speakerTime.push([participant, time])
            //         } else {
            //             this.speakerTime[index][1] += time
            //         }
            //     }
            // })
            const speakerTimes = new Map();

            for (let i = 0; i < this.callTranscription.utterances.length; i++) {
                // console.log(this.callTranscription.utterances[i])
                const speaker = this.callTranscription.utterances[i].speaker;
                const startTime = this.callTranscription.utterances[i].start;
                const endTime = this.callTranscription.utterances[i].end;

                const talkTime = endTime - startTime;
                // console.log('talkTime', talkTime)

                if (speakerTimes.has(speaker)) {
                    speakerTimes.set(speaker, speakerTimes.get(speaker) + talkTime);
                } else {
                    speakerTimes.set(speaker, talkTime);
                }
            }

            this.speakerTime = Array.from(speakerTimes, ([name, value]) => [`Speaker ${name + 1}`, (value / this.callTranscription.duration * 100).toFixed(2)]);

            // this is used to figure out the silence time (remaining percentage)
            this.speakerTime.push(['Silence/Hold', (100 - this.speakerTime.reduce((accumulator, currentElement) => accumulator + parseFloat(currentElement[1]), 0)).toFixed(2)]);
        },
        setTimeInSeconds(secs) {
            const millis = secs * 1000;
            const minutes = Math.floor(millis / 60000);
            const seconds = ((millis % 60000) / 1000).toFixed(0);
            return minutes + ":" + (parseInt(seconds) < 10 ? '0' : '') + seconds;
        },
        setTimeOfPlayback(secs) {
            this.autoScrollEnabled = true;
            const millis = secs * 1000;
            const seconds = millis / 1000;
            this.audio.currentTime = seconds.toFixed(0);
            this.audio.play();
        },

        toggleDisplayType() {
          if(this.displayStyle === 'normal') {
              this.displayStyle = 'flipped';
              this.updateCharts();
              return
          }
          if(this.displayStyle === 'flipped') {
              this.displayStyle = 'normal';
              this.updateCharts();
              return
          }
        },
        updateCharts() {
            this.sentiments = [];
            this.speakerTime = [];
            this.setSpeakerTime();
        },
        update() {
            this.currentSeconds = parseInt(this.audio.currentTime);
        },
    },
    computed: {
        displayConfidence() {
            return this.callTranscription.results.utterances[0].confidence !== 0
        },
        validTranscription() {
            return this.callTranscription?.utterances?.length > 0;
        },
        validSpeakerTime() {
            return true
        }
    },
    components: {
        SentimentIcon
    },
    props: {
        callTranscription: Object,
        conference: Object,
        hideSpeakerTimeChart: Boolean,
        isLoading: Boolean,
        isOutlined: Boolean,
    },
    watch: {
        playing(value) {
            if (value) { return this.audio.play(); }
            this.audio.pause();
        },
        currentSeconds() {
            if (!this.autoScrollEnabled) return;
            this.$nextTick(() => {
                const container = document.getElementById('transcription-holder');
                if (!container) return;
                const active = container.querySelector('.active-segment');
                if (active) {
                    // Smoothly scroll only the container so the active segment is at the top
                    const targetScroll = active.offsetTop - container.offsetTop;
                    // Animate scrollTop
                    this.animateScroll(container, targetScroll, 300);
                }
            });
        },
    },
    created() {
        this.setSpeakerTime();
    },
    destroyed() {
        const transcriptionHolder = document.getElementById('transcription-holder');
        if (transcriptionHolder) {
            transcriptionHolder.removeEventListener('scroll', this.onTranscriptionScroll);
            transcriptionHolder.removeEventListener('wheel', this.onUserInteract);
            transcriptionHolder.removeEventListener('touchstart', this.onUserInteract);
            transcriptionHolder.removeEventListener('mousedown', this.onUserInteract);
        }
        if (this.userInteractTimeout) {
            clearTimeout(this.userInteractTimeout);
        }
    },
    mounted() {
        this.audio = document.getElementById('audio-playback');
        if(this.audio) {
            this.audio.addEventListener('timeupdate', this.update);
            this.audio.addEventListener('loadeddata', this.load);
            this.audio.addEventListener('pause', () => { this.playing = false; });
            this.audio.addEventListener('play', () => { this.playing = true; });
        }
        // Add scroll and user interaction event listeners to transcription-holder
        const transcriptionHolder = document.getElementById('transcription-holder');
        if (transcriptionHolder) {
            transcriptionHolder.addEventListener('scroll', this.onTranscriptionScroll);
            transcriptionHolder.addEventListener('wheel', this.onUserInteract);
            transcriptionHolder.addEventListener('touchstart', this.onUserInteract);
            transcriptionHolder.addEventListener('mousedown', this.onUserInteract);
        }
    },
})
</script>

<style lang="scss" scoped>
    #app {
        .grey-side-border {
            border-right: 1px var(--off-white) solid;
            border-left: 1px var(--off-white) solid;
        }

    }
</style>
